import { fromEvent, Subscription } from 'rxjs';

import { getOverlayPaintSerial } from './drawUnit';
import { type OverlayHitRegistry } from './OverlayHitRegistry';
import {
  isClickParticipant,
  isHoverParticipant,
  isPointerParticipant,
} from './overlayLayerPolicy';
import {
  type OverlayHitResult,
  type OverlayHitTestContext,
  type OverlayHostBinding,
  type OverlayHoverState,
  type OverlayLayerEntry,
} from './type';

/** release 클릭이 허용하는 기본 최대 이탈 거리(CSS px). 시뮬레이션 선택 클릭과 같은 기준입니다. */
const DEFAULT_CLICK_MAX_EXCURSION = 8;
/** 소비한 pointerup 이후 흡수할 click/dblclick 의 좌표 허용 오차(CSS px)와 허용 시간(ms). */
const ABSORB_MAX_DISTANCE = 4;
const ABSORB_MAX_AGE_MS = 500;

export type OverlayPointerDispatcherDeps = {
  binding: OverlayHostBinding;
  getLayerList: () => readonly OverlayLayerEntry[];
  getLayerMap: () => ReadonlyMap<string, OverlayLayerEntry>;
  getLayerVersion: () => number;
  getPaintSerial: () => number;
  registry: OverlayHitRegistry;
  requestRedraw: () => void;
};

type Candidate = {
  hit: OverlayHitResult;
  layer: OverlayLayerEntry;
  /** 실제로 그린 순번. 동적 hitTest 는 0 이라 같은 priority 에서 등록 대상보다 뒤로 밀립니다. */
  order: number;
};

/** release 클릭으로 소비한 pointerdown 시퀀스. 이 시퀀스의 up/cancel/click 만 흡수합니다. */
type ReleaseSequence = {
  hitId: string;
  layerId: string;
  maxExcursionSq: number;
  pointerId: number;
  startX: number;
  startY: number;
};

/** 소비한 pointerup 의 위치와 시각. 뒤따르는 click/dblclick 이 이 시퀀스의 것인지 가립니다. */
type AbsorbRecord = {
  /** 이 시퀀스의 click 을 이미 흡수했는지. pointerId 가 없는 dblclick 은 이 뒤에만 흡수합니다. */
  isClickAbsorbed: boolean;
  pointerId: number;
  time: number;
  x: number;
  y: number;
};

/** 마지막 평가 입력(좌표·도장 세대·레이어 버전). 같은 값이면 평가를 건너뜁니다. */
type EvalKey = {
  layerVersion: number;
  paintSerial: number;
  x: number;
  y: number;
};

const toHitKey = (layerId: string, hitId: string) => `${layerId}\u0000${hitId}`;

/**
 * 오버레이 root 에 리스너를 종류별 한 개만 거는 단일 포인터 디스패처.
 * 판정은 엔진의 레이어·판정 데이터를 읽어 하고, 호버·커서·클릭을 레이어 옵션으로 전달합니다.
 *
 * - `pointerdown` 은 호스트 수명 동안 capture 로 한 개를 유지합니다.
 * - move/up/leave/cancel 과 window blur 는 호버나 release 클릭에 참여하는 레이어가 0↔1 로
 *   바뀔 때 붙이고 뗍니다.
 * - 이동은 가장 최근 이벤트만 남겨 rAF 에서 한 번 평가합니다.
 */
export const createOverlayPointerDispatcher = (
  deps: OverlayPointerDispatcherDeps,
) => {
  const { binding, registry } = deps;

  let root: Nullish<HTMLElement> = null;
  let isPointerListening = false;
  let frameId: Nullish<number> = null;
  const pointer: {
    clientX: number;
    clientY: number;
    event: Nullish<PointerEvent>;
  } = { clientX: 0, clientY: 0, event: null };

  let hover: Nullish<OverlayHoverState> = null;
  let hoverKey: Nullish<string> = null;
  const evalKey: EvalKey = {
    layerVersion: -1,
    paintSerial: -1,
    x: Number.NaN,
    y: Number.NaN,
  };

  // 커서: 이 시스템이 쓴 값과 획득 전 값을 기억해, 현재 값이 쓴 값과 같을 때만 되돌립니다.
  let cursorCanvas: Nullish<HTMLCanvasElement> = null;
  let cursorOwned: Nullish<string> = null;
  let cursorSaved = '';
  // 같은 대상 위에서 외부가 커서를 바꿨으면 대상이 바뀔 때까지 다시 덮어쓰지 않습니다.
  let cursorSuppressedKey: Nullish<string> = null;

  let release: Nullish<ReleaseSequence> = null;
  let isAbsorbing = false;
  let isAbsorberAttached = false;
  let absorbFrameId: Nullish<number> = null;
  let absorbRecord: Nullish<AbsorbRecord> = null;
  // release 시퀀스가 진행되는 동안만 document 에 거는 관찰 리스너(이탈 거리 추적·바깥 up 정리)입니다.
  let trackedDocument: Nullish<Document> = null;
  let documentSubscription = new Subscription();

  const isInputBlocked = () => binding.isInputEnabled?.() === false;

  // ---------------------------------------------------------------- 판정

  /** 클릭·소비자 콜백이 외부에 보관해도 이후 이벤트로 바뀌지 않도록 매번 새 컨텍스트를 만듭니다. */
  const createHitContext = (
    event: PointerEvent,
    x: number,
    y: number,
  ): Nullish<OverlayHitTestContext> => {
    const camera = binding.getCamera();
    const overlayCanvas = binding.getOverlayCanvas('top');
    const viewport = binding.getViewport();
    if (!camera || !overlayCanvas) return null;
    if (viewport.cssW === 0 || viewport.cssH === 0) return null;
    const pixelRatio = window.devicePixelRatio || 1;
    return {
      bufH: Math.round(viewport.cssH * pixelRatio),
      bufW: Math.round(viewport.cssW * pixelRatio),
      camera,
      canvas: overlayCanvas,
      cssH: viewport.cssH,
      cssW: viewport.cssW,
      event,
      halfH: viewport.cssH / 2,
      halfW: viewport.cssW / 2,
      paintSerial: getOverlayPaintSerial(),
      pixelRatio,
      timeMs: performance.now(),
      viewport: {
        height: viewport.cssH,
        left: viewport.left ?? 0,
        top: viewport.top ?? 0,
        width: viewport.cssW,
      },
      x,
      y,
    };
  };

  /**
   * 한 레이어의 최상위 대상. `hitTest` 콜백은 레지스트리 경계로 생략하지 않고 항상 부릅니다.
   * 콜백이 없는 레이어는 도장에서 기록된 대상만 경계 상자 안에서 검색합니다.
   */
  const probeLayer = (
    layer: OverlayLayerEntry,
    context: OverlayHitTestContext,
  ): Nullish<Candidate> => {
    if (layer.hitTest) {
      const hit = layer.hitTest(context);
      return hit ? { hit, layer, order: 0 } : null;
    }
    const bounds = registry.getBounds(layer.id);
    if (!bounds) return null;
    if (
      context.x < bounds.minX ||
      context.x > bounds.maxX ||
      context.y < bounds.minY ||
      context.y > bounds.maxY
    ) {
      return null;
    }
    const entry = registry.findTop(layer.id, context.x, context.y);
    if (!entry) return null;
    return {
      hit: { id: entry.target.id, payload: entry.target.payload },
      layer,
      order: entry.order,
    };
  };

  /**
   * priority 가 높은 레이어부터 판정하고 첫 hit 이 나온 priority 에서 멈춥니다. 같은 priority 안에서는
   * 실제로 나중에 그린 대상이 우선이고, 동적 hitTest 끼리는 나중에 등록된 레이어가 우선입니다.
   */
  const pickTop = (
    context: OverlayHitTestContext,
    isEligible: (layer: OverlayLayerEntry) => boolean,
  ): Nullish<Candidate> => {
    const layerList = deps.getLayerList();
    let index = 0;
    while (index < layerList.length) {
      const { priority } = layerList[index];
      let best: Nullish<Candidate> = null;
      for (
        ;
        index < layerList.length && layerList[index].priority === priority;
        index += 1
      ) {
        const layer = layerList[index];
        if (!isEligible(layer)) continue;
        const candidate = probeLayer(layer, context);
        if (candidate && (!best || candidate.order > best.order)) {
          best = candidate;
        }
      }
      if (best) return best;
    }
    return null;
  };

  // ---------------------------------------------------------------- 커서

  const releaseCursor = () => {
    if (cursorOwned != null && cursorCanvas) {
      if (cursorCanvas.style.cursor === cursorOwned) {
        cursorCanvas.style.cursor = cursorSaved;
      }
    }
    cursorOwned = null;
    cursorCanvas = null;
  };

  const applyCursor = (desired: Nullish<string>) => {
    if (desired == null) {
      releaseCursor();
      return;
    }
    const canvas = binding.getCanvasElement();
    if (!canvas) return;
    if (cursorOwned != null) {
      if (cursorCanvas !== canvas) {
        releaseCursor();
      } else if (canvas.style.cursor !== cursorOwned) {
        // 다른 코드가 커서를 바꿨습니다. 덮어쓰지 않고 소유를 놓습니다.
        cursorOwned = null;
        cursorCanvas = null;
        cursorSuppressedKey = hoverKey;
        return;
      } else {
        if (desired !== cursorOwned) {
          canvas.style.cursor = desired;
          cursorOwned = desired;
        }
        return;
      }
    }
    if (hoverKey != null && cursorSuppressedKey === hoverKey) return;
    cursorSaved = canvas.style.cursor;
    canvas.style.cursor = desired;
    cursorOwned = desired;
    cursorCanvas = canvas;
  };

  // ---------------------------------------------------------------- 호버

  const clearHover = () => {
    cursorSuppressedKey = null;
    if (!hover) {
      releaseCursor();
      return;
    }
    const previous = hover;
    hover = null;
    hoverKey = null;
    deps.getLayerMap().get(previous.layerId)?.onHoverChange?.(null);
    releaseCursor();
    deps.requestRedraw();
  };

  const applyHover = (found: Nullish<Candidate>) => {
    if (!found) {
      clearHover();
      return;
    }
    const { hit, layer } = found;
    const nextKey = toHitKey(layer.id, hit.id);
    if (nextKey === hoverKey) {
      // 같은 대상. 최신 payload 만 갱신하고 콜백·다시 그리기는 생략합니다.
      if (hover && hover.payload !== hit.payload) {
        hover = { id: hit.id, layerId: layer.id, payload: hit.payload };
      }
      applyCursor(layer.cursor?.(hit));
      return;
    }
    const previous = hover;
    hover = { id: hit.id, layerId: layer.id, payload: hit.payload };
    hoverKey = nextKey;
    cursorSuppressedKey = null;
    // 다른 레이어로 옮겨가면 이전 레이어에 null 을 한 번 알립니다. 같은 레이어 안에서 대상이
    // 바뀌면 null 없이 새 대상만 알립니다.
    if (previous && previous.layerId !== layer.id) {
      deps.getLayerMap().get(previous.layerId)?.onHoverChange?.(null);
    }
    layer.onHoverChange?.(hit);
    applyCursor(layer.cursor?.(hit));
    deps.requestRedraw();
  };

  // ---------------------------------------------------------------- 이동 평가

  const cancelPending = () => {
    if (frameId == null) return;
    cancelAnimationFrame(frameId);
    frameId = null;
  };

  const forgetPointer = () => {
    pointer.event = null;
    evalKey.layerVersion = -1;
  };

  const evaluate = () => {
    const event = pointer.event;
    if (!isPointerListening || !event) return;
    const canvas = binding.getCanvasElement();
    if (!canvas || isInputBlocked()) {
      clearHover();
      evalKey.layerVersion = -1;
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const x = pointer.clientX - rect.left;
    const y = pointer.clientY - rect.top;
    const paintSerial = deps.getPaintSerial();
    const layerVersion = deps.getLayerVersion();
    // 좌표·도장 세대·레이어 버전이 모두 같으면 같은 결과이므로 반환합니다.
    if (
      evalKey.x === x &&
      evalKey.y === y &&
      evalKey.paintSerial === paintSerial &&
      evalKey.layerVersion === layerVersion
    ) {
      return;
    }
    const context = createHitContext(event, x, y);
    if (!context) {
      clearHover();
      evalKey.layerVersion = -1;
      return;
    }
    evalKey.layerVersion = layerVersion;
    evalKey.paintSerial = paintSerial;
    evalKey.x = x;
    evalKey.y = y;
    applyHover(pickTop(context, isHoverParticipant));
  };

  const scheduleEval = () => {
    if (frameId != null) return;
    frameId = requestAnimationFrame(() => {
      frameId = null;
      evaluate();
    });
  };

  // ---------------------------------------------------------------- release 클릭

  /**
   * 소비한 pointerup 직후 같은 위치·시각에 오는 click/dblclick 만 흡수합니다. 좌표나 시각이 어긋난
   * click(키보드 click, 시간이 지난 뒤의 click 등)은 이 시퀀스의 것이 아니므로 통과시킵니다.
   */
  const absorbEvent = (event: Event) => {
    if (!isAbsorbing || !absorbRecord) return;
    // 실제 3D 캔버스가 아닌 root 안의 다른 DOM 요소의 click 은 이 시퀀스의 것이 아닙니다.
    if (event.target !== binding.getCanvasElement()) return;
    const { clientX, clientY } = event as MouseEvent;
    if (event.timeStamp - absorbRecord.time > ABSORB_MAX_AGE_MS) return;
    if (
      Math.hypot(clientX - absorbRecord.x, clientY - absorbRecord.y) >
      ABSORB_MAX_DISTANCE
    ) {
      return;
    }
    const { pointerId } = event as Partial<PointerEvent>;
    if (typeof pointerId === 'number') {
      // pointerId 가 있으면 소유 pointer 의 것만 흡수합니다.
      if (pointerId !== absorbRecord.pointerId) return;
    } else if (event.type === 'dblclick' && !absorbRecord.isClickAbsorbed) {
      // 기존 MouseEvent dblclick 은 pointerId 가 없으므로 소유 click 을 먼저 흡수한 뒤에만 흡수합니다.
      return;
    }
    if (event.type === 'click') absorbRecord.isClickAbsorbed = true;
    event.stopPropagation();
  };

  const trackExcursion = (event: PointerEvent, sequence: ReleaseSequence) => {
    const dx = event.clientX - sequence.startX;
    const dy = event.clientY - sequence.startY;
    sequence.maxExcursionSq = Math.max(
      sequence.maxExcursionSq,
      dx * dx + dy * dy,
    );
  };

  /** 소비 중인 시퀀스의 이동을 root 밖에서도 셉니다(관찰만 하며 전파를 막지 않습니다). */
  const handleDocumentMove = (event: PointerEvent) => {
    if (release && event.pointerId === release.pointerId) {
      trackExcursion(event, release);
    }
  };

  /**
   * root 밖에서 놓거나 취소된 시퀀스는 클릭이 아니므로 흡수 상태만 정리합니다. capture 로 받기 때문에
   * root 안의 이벤트는 root 핸들러(finishRelease/handlePointerCancel)가 처리하도록 넘깁니다.
   */
  const handleDocumentEnd = (event: PointerEvent) => {
    if (!release || event.pointerId !== release.pointerId) return;
    if (root && event.target instanceof Node && root.contains(event.target)) {
      return;
    }
    release = null;
    endSequence();
  };

  const startDocumentTracking = (target: Document) => {
    if (trackedDocument) return;
    trackedDocument = target;
    // 무관한 UI 가 bubble 단계에서 전파를 막아도 종료를 놓치지 않도록 모두 capture 로 관찰합니다.
    documentSubscription.add(
      fromEvent<PointerEvent>(target, 'pointermove', {
        capture: true,
      }).subscribe(handleDocumentMove),
    );
    documentSubscription.add(
      fromEvent<PointerEvent>(target, 'pointerup', {
        capture: true,
      }).subscribe(handleDocumentEnd),
    );
    documentSubscription.add(
      fromEvent<PointerEvent>(target, 'pointercancel', {
        capture: true,
      }).subscribe(handleDocumentEnd),
    );
  };

  const stopDocumentTracking = () => {
    if (!trackedDocument) return;
    trackedDocument = null;
    documentSubscription.unsubscribe();
    documentSubscription = new Subscription();
  };

  const endSequence = () => {
    isAbsorbing = false;
    absorbRecord = null;
    stopDocumentTracking();
    if (absorbFrameId != null) {
      cancelAnimationFrame(absorbFrameId);
      absorbFrameId = null;
    }
    if (!isAbsorberAttached || !root) return;
    root.removeEventListener('click', absorbEvent, true);
    root.removeEventListener('dblclick', absorbEvent, true);
    isAbsorberAttached = false;
  };

  const beginRelease = (event: PointerEvent, found: Candidate) => {
    release = {
      hitId: found.hit.id,
      layerId: found.layer.id,
      maxExcursionSq: 0,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    isAbsorbing = true;
    absorbRecord = null;
    if (root && !isAbsorberAttached) {
      root.addEventListener('click', absorbEvent, true);
      root.addEventListener('dblclick', absorbEvent, true);
      isAbsorberAttached = true;
    }
    if (root) startDocumentTracking(root.ownerDocument);
    event.stopPropagation();
  };

  /** 소비한 시퀀스의 pointerup. 같은 대상 위이고 이탈 거리가 한도 이내일 때만 클릭을 확정합니다. */
  const finishRelease = (event: PointerEvent, sequence: ReleaseSequence) => {
    release = null;
    stopDocumentTracking();
    absorbRecord = {
      isClickAbsorbed: false,
      pointerId: sequence.pointerId,
      time: event.timeStamp,
      x: event.clientX,
      y: event.clientY,
    };
    event.stopPropagation();
    // click/dblclick 은 up 직후 같은 태스크에서 오므로 다음 프레임까지만 흡수합니다.
    absorbFrameId = requestAnimationFrame(() => {
      absorbFrameId = null;
      endSequence();
    });
    // 종료 up 의 button 이 왼쪽이 아니면(다중 버튼 chord 의 마지막 해제 등) 클릭이 아닙니다.
    if (event.button !== 0) return;
    const canvas = binding.getCanvasElement();
    if (!canvas || event.target !== canvas || isInputBlocked()) return;
    trackExcursion(event, sequence);
    const rect = canvas.getBoundingClientRect();
    // 컨텍스트 이벤트는 놓은 시점의 pointerup 이라 수정키도 놓은 시점 기준입니다.
    const context = createHitContext(
      event,
      event.clientX - rect.left,
      event.clientY - rect.top,
    );
    if (!context) return;
    const found = pickTop(context, isClickParticipant);
    if (!found) return;
    if (found.layer.id !== sequence.layerId) return;
    if (found.hit.id !== sequence.hitId) return;
    const limit = found.layer.clickMaxExcursion ?? DEFAULT_CLICK_MAX_EXCURSION;
    if (sequence.maxExcursionSq > limit * limit) return;
    found.layer.onClick?.(found.hit, context);
  };

  // ---------------------------------------------------------------- 이벤트 핸들러

  const handlePointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    // 다른 pointer 의 down 은 소유 중인 release 시퀀스를 건드리지 않고 그대로 통과시킵니다.
    if (release && event.pointerId !== release.pointerId) return;
    // 새 시퀀스가 시작되면 이전 시퀀스의 흡수 상태를 물려받지 않습니다.
    release = null;
    endSequence();
    cancelPending();
    const canvas = binding.getCanvasElement();
    if (!canvas || event.target !== canvas || isInputBlocked()) return;
    const rect = canvas.getBoundingClientRect();
    const context = createHitContext(
      event,
      event.clientX - rect.left,
      event.clientY - rect.top,
    );
    if (!context) return;
    const found = pickTop(context, isClickParticipant);
    if (!found) return;
    if (found.layer.clickTrigger === 'release') {
      beginRelease(event, found);
      return;
    }
    found.layer.onClick?.(found.hit, context);
    event.stopPropagation();
  };

  const resetPointerState = () => {
    cancelPending();
    clearHover();
    forgetPointer();
    release = null;
    endSequence();
  };

  const handlePointerMove = (event: PointerEvent) => {
    if (release && event.pointerId === release.pointerId) {
      trackExcursion(event, release);
    }
    // 1. 포인터 레이어가 없다(해제 직전 이벤트 방어).
    if (!isPointerListening) return;
    // 2. 터치는 호버가 없다.
    if (event.pointerType === 'touch') return;
    // 3. 버튼을 누른 이동은 카메라 조작·드래그이므로 호버를 해제한다.
    // 4. 3D 캔버스가 아닌 겹친 DOM UI 위 이동도 호버를 해제한다.
    if (event.buttons !== 0 || event.target !== binding.getCanvasElement()) {
      cancelPending();
      clearHover();
      forgetPointer();
      return;
    }
    // 5. 가장 최근 이벤트만 남겨 프레임당 한 번 평가한다.
    pointer.clientX = event.clientX;
    pointer.clientY = event.clientY;
    pointer.event = event;
    scheduleEval();
  };

  const handlePointerUp = (event: PointerEvent) => {
    if (release && event.pointerId === release.pointerId) {
      finishRelease(event, release);
    }
    if (!isPointerListening || event.pointerType === 'touch') return;
    if (event.target !== binding.getCanvasElement()) return;
    pointer.clientX = event.clientX;
    pointer.clientY = event.clientY;
    pointer.event = event;
    scheduleEval();
  };

  const handlePointerCancel = (event: PointerEvent) => {
    if (release) {
      // 다른 pointer 의 cancel 은 소유 시퀀스와 무관하므로 그대로 통과시킵니다.
      if (event.pointerId !== release.pointerId) return;
      event.stopPropagation();
    }
    resetPointerState();
  };

  const handlePointerLeave = () => {
    cancelPending();
    clearHover();
    forgetPointer();
  };

  const setPointerListening = (next: boolean) => {
    if (!root || next === isPointerListening) return;
    isPointerListening = next;
    if (next) {
      root.addEventListener('pointermove', handlePointerMove);
      root.addEventListener('pointerup', handlePointerUp, true);
      root.addEventListener('pointerleave', handlePointerLeave);
      root.addEventListener('pointercancel', handlePointerCancel, true);
      window.addEventListener('blur', resetPointerState);
      return;
    }
    root.removeEventListener('pointermove', handlePointerMove);
    root.removeEventListener('pointerup', handlePointerUp, true);
    root.removeEventListener('pointerleave', handlePointerLeave);
    root.removeEventListener('pointercancel', handlePointerCancel, true);
    window.removeEventListener('blur', resetPointerState);
    resetPointerState();
  };

  const hasPointerParticipant = () =>
    deps.getLayerList().some(isPointerParticipant);

  return {
    /** root 에 포인터 리스너를 붙입니다. 이미 붙어 있으면 아무것도 하지 않습니다. */
    attach: (target: HTMLElement) => {
      if (root) return;
      root = target;
      root.addEventListener('pointerdown', handlePointerDown, true);
      setPointerListening(hasPointerParticipant());
    },
    /** 모든 리스너를 떼고 대기 평가·호버·커서·흡수 상태를 정리합니다. */
    detach: () => {
      if (!root) return;
      if (isPointerListening) setPointerListening(false);
      else resetPointerState();
      root.removeEventListener('pointerdown', handlePointerDown, true);
      root = null;
    },
    getHover: (): Nullish<OverlayHoverState> => hover,
    /** 3D 캔버스가 교체·소멸됐을 때. 호버를 해제하고 대기 평가를 취소합니다. */
    handleCanvasChange: () => resetPointerState(),
    /**
     * 레이어가 바뀌기 직전(엔진 맵 변경 전)에 부릅니다. 호버 중이던 레이어가 해제되거나 호버에서 빠지면
     * 이전 레이어의 콜백으로 호버를 해제하고 커서를 되돌립니다.
     */
    handleLayerChange: (layerId: string, next: Nullish<OverlayLayerEntry>) => {
      if (hover?.layerId === layerId && (!next || !isHoverParticipant(next))) {
        clearHover();
      }
      if (
        release?.layerId === layerId &&
        (!next || !isClickParticipant(next))
      ) {
        release = null;
        endSequence();
      }
    },
    /** 도장이 끝났습니다. 정지한 포인터 아래 대상이 바뀌었을 수 있으니 마지막 위치를 다시 판정합니다. */
    handlePaintCommitted: () => {
      if (isPointerListening && pointer.event) scheduleEval();
    },
    /** 레이어 구성이 바뀐 뒤 move/up/leave/cancel 리스너를 0↔1 로 맞춥니다. */
    syncParticipation: () => setPointerListening(hasPointerParticipant()),
  };
};

export type OverlayPointerDispatcher = ReturnType<
  typeof createOverlayPointerDispatcher
>;
